import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Train, Loader2, X } from "lucide-react";
import { apiClient } from "@/App";

/**
 * StationAutocomplete — Places-backed input biased to train stations.
 * Props:
 *  - value: { name, place_id, lat, lng } | null
 *  - onChange: (next | null) => void
 *  - placeholder?: string
 *  - testId?: string
 *
 * When the user picks a prediction we resolve its details (lat/lng) via
 * /maps/places/details and emit the structured object.
 * Free-typed text without a pick clears the structured value — the user
 * must select from the dropdown for the journey calc to use coordinates.
 */
export const StationAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const [query, setQuery] = useState(value?.name || "");
  const [predictions, setPredictions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [resolving, setResolving] = useState(false);
  const wrapperRef = useRef(null);
  // True only after the user actually types — gates the search so a programmatic
  // value (saved station pre-filled on mount, or set after a pick) never opens
  // the dropdown on its own.
  const userEditedRef = useRef(false);

  // Keep input in sync when parent value changes externally (e.g. on initial fetch)
  useEffect(() => {
    userEditedRef.current = false;
    setQuery(value?.name || "");
  }, [value?.name]);

  // Debounced autocomplete — only triggered by query changes, not value changes
  // Note: No types filter — Google Places Autocomplete doesn't support train_station filter.
  // Users will search by station name (e.g. "Reading", "Paddington") and get station results.
  useEffect(() => {
    if (!query || query.length < 2) {
      setPredictions([]);
      return;
    }
    // Only search/open after the user types — otherwise the pre-filled saved
    // value would auto-drop the dropdown the instant the Travel tab renders
    // (or right after picking a station re-sets the query).
    if (!userEditedRef.current) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiClient.get(
          `/maps/places/autocomplete?q=${encodeURIComponent(query)}`
        );
        setPredictions(res.data?.predictions || []);
        setShowDropdown(true);
      } catch (err) {
        console.error("Station autocomplete error:", err);
        setPredictions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // Click-outside closes dropdown
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handlePick = async (pred) => {
    setShowDropdown(false);
    userEditedRef.current = false;  // prevent the value-sync re-search from reopening
    setResolving(true);
    try {
      const res = await apiClient.get(`/maps/places/details?place_id=${pred.place_id}`);
      const d = res.data?.result;
      if (d) {
        const next = {
          name: d.name || pred.main_text || pred.description,
          place_id: pred.place_id,
          lat: d.lat ?? null,
          lng: d.lng ?? null,
        };
        setQuery(next.name);
        onChange(next);
      } else {
        // Fallback to name-only if details failed
        const next = {
          name: pred.main_text || pred.description,
          place_id: pred.place_id,
          lat: null,
          lng: null,
        };
        setQuery(next.name);
        onChange(next);
      }
    } catch (err) {
      console.error("Station details error:", err);
    } finally {
      setResolving(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setPredictions([]);
    onChange(null);
  };

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    userEditedRef.current = true;  // user is actively typing — allow search/open
    setQuery(newQuery);
    // Typing diverges from the resolved value — clear structured selection
    if (value && newQuery !== value.name) {
      onChange(null);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Train className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder || "Start typing a station name..."}
          className="pl-9 pr-9"
          data-testid={testId}
        />
        {(searching || resolving) && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {!searching && !resolving && query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted/50"
            aria-label="Clear station"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-y-auto">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => handlePick(p)}
              className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-start gap-2 border-b border-border last:border-0"
            >
              <Train className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.main_text}</div>
                {p.secondary_text && (
                  <div className="text-xs text-muted-foreground truncate">{p.secondary_text}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {value?.place_id && value?.lat != null && (
        <p className="text-xs text-emerald-500 mt-1">
          ✓ Exact location pinned — drive time will use precise station coordinates
        </p>
      )}
    </div>
  );
};

export default StationAutocomplete;
